<template>
  <div class="d-flex flex-column min-vh-100">
    <PriorHeader />

    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-12 mt-16">
            <h1 class="text-primary weight-600">{{ passwordUpdated ? "Password Changed" : "Reset Password" }}</h1>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <div v-if="!passwordUpdated">
                <v-form @submit.prevent="updatePassword">
                  <p class="text-center mb-4 text-primary weight-500 lh--force-white-background">
                    To complete the password reset process, please enter your new password below and confirm.
                  </p>

                  <v-text-field
                    v-model="newPassword"
                    label="New Password"
                    :type="showPassword ? 'text' : 'password'"
                    :error-messages="getPasswordErrorMessage"
                    class="mb-4 lh--force-white-background"
                    :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append-inner="showPassword = !showPassword" />

                  <v-text-field
                    v-model="passwordConfirmation"
                    label="Confirm Password"
                    :type="showPassword ? 'text' : 'password'"
                    :error-messages="getPasswordMatchErrorMessage"
                    class="mb-4 lh--force-white-background"
                    :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append-inner="showPassword = !showPassword" />

                  <v-btn color="primary" block size="large" type="submit" class="mb-4 mt-8" :loading="showPreloader">
                    Reset Password
                  </v-btn>

                  <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                  </v-alert>
                </v-form>
              </div>

              <div v-else class="text-center">
                <v-alert type="success" variant="tonal" class="mb-4"> Password changed successfully! </v-alert>
                <v-btn color="primary" block size="large" @click="redirectionToLoginPage"> Continue To Login </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <PriorFooter />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import PriorHeader from "@/components/PriorHeader";
import PriorFooter from "@/components/PriorFooter";
import authService from "@/services/authService";
import { useRoute, useRouter } from "vue-router";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";

const errors = ref([]);
const showPreloader = ref(false);
const showPassword = ref(false);
const route = useRoute();
const router = useRouter();

const formButtonClicked = ref(false);
const passwordUpdated = ref(false);

const newPassword = ref("");
const passwordConfirmation = ref("");

// Validators
const isPasswordValid = computed(() => {
  const password = newPassword.value;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

const isPasswordMatchValid = computed(() => {
  return newPassword.value === passwordConfirmation.value;
});

const getPasswordErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!newPassword.value) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const getPasswordMatchErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!passwordConfirmation.value) return "Please confirm your password";
  if (!isPasswordMatchValid.value) return "Passwords must match";
  return "";
});

const updatePassword = async () => {
  formButtonClicked.value = true;

  if (isPasswordValid.value && isPasswordMatchValid.value) {
    showPreloader.value = true;
    try {
      await authService.passwordReset(newPassword.value, route.params.token);
      passwordUpdated.value = true;
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    } finally {
      showPreloader.value = false;
    }
  }
};

const redirectionToLoginPage = () => {
  router.push({ name: "login" });
};
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";
</style>
